/**
 * Admin (Pages CMS) SEO overrides.
 *
 * Each managed page has optional meta_title / meta_description /
 * canonical_url / schema_json values in the `pages` table. When a value is
 * set, it wins over whatever the route file hardcodes. When it's empty, the
 * coded default is used unchanged.
 *
 * `applySeoOverrides` takes the head object a route already builds and returns
 * a new one with the overrides swapped in — it never mutates its input.
 */

export type PageSeoOverride = {
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalUrl: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schemaJson: any | null;
};

type MetaEntry = Record<string, string | undefined>;
type LinkEntry = Record<string, string | undefined>;
type ScriptEntry = Record<string, string | undefined>;

export type HeadObject = {
  meta: MetaEntry[];
  links?: LinkEntry[];
  scripts?: ScriptEntry[];
};

function clean(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t.length > 0 ? t : null;
}

/** Replace (or append) a meta entry matched by `title`, `name`, or `property`. */
function setMeta(meta: MetaEntry[], match: MetaEntry, next: MetaEntry): MetaEntry[] {
  const key = Object.keys(match)[0] as string;
  const value = match[key];
  const idx = meta.findIndex((m) =>
    key === "title" ? "title" in m : m[key] === value,
  );
  if (idx === -1) return [...meta, next];
  const out = meta.slice();
  out[idx] = { ...out[idx], ...next };
  return out;
}

export function applySeoOverrides<T extends HeadObject>(
  head: T,
  override?: PageSeoOverride | null,
): T {
  if (!override) return head;

  const title = clean(override.metaTitle);
  const description = clean(override.metaDescription);
  const canonicalUrl = clean(override.canonicalUrl);
  const schema = override.schemaJson ?? null;

  if (!title && !description && !canonicalUrl && !schema) return head;

  let meta = head.meta.slice();

  if (title) {
    meta = setMeta(meta, { title: "" }, { title });
    meta = setMeta(meta, { property: "og:title" }, { property: "og:title", content: title });
    meta = setMeta(meta, { name: "twitter:title" }, { name: "twitter:title", content: title });
  }

  if (description) {
    meta = setMeta(meta, { name: "description" }, { name: "description", content: description });
    meta = setMeta(
      meta,
      { property: "og:description" },
      { property: "og:description", content: description },
    );
    meta = setMeta(
      meta,
      { name: "twitter:description" },
      { name: "twitter:description", content: description },
    );
  }

  let links = head.links ? head.links.slice() : undefined;
  if (canonicalUrl) {
    meta = setMeta(meta, { property: "og:url" }, { property: "og:url", content: canonicalUrl });
    if (links) {
      const idx = links.findIndex((l) => l["rel"] === "canonical");
      if (idx === -1) links.push({ rel: "canonical", href: canonicalUrl });
      else links[idx] = { ...links[idx], href: canonicalUrl };
    } else {
      links = [{ rel: "canonical", href: canonicalUrl }];
    }
  }

  let scripts = head.scripts ? head.scripts.slice() : undefined;
  if (schema) {
    const entry = { type: "application/ld+json", children: JSON.stringify(schema) };
    if (!scripts || scripts.length === 0) {
      scripts = [entry];
    } else {
      const idx = scripts.findIndex((s) => s["type"] === "application/ld+json");
      if (idx === -1) scripts.push(entry);
      else scripts[idx] = entry;
    }
  }

  return { ...head, meta, ...(links ? { links } : {}), ...(scripts ? { scripts } : {}) };
}
